import React, { FormEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Loader } from '@components';
import Avatar from '@components/CRM/Clients/Avatar';
import Button from '@components/ButtonTs';
import Input from '@components/Input';
import Select from '@components/Select';
import { ArrowRight, Check, Plus } from '@icons/common';

import { AGENT_TEAM_ROUTE } from './routes';
import {
  B2BTeamMember,
  B2BTeamMemberDraft,
  B2BTeamMemberRole,
  B2BTeamMemberStatus,
} from './types';
import {
  createB2BTeamMemberAction,
  deleteB2BTeamMemberAction,
  fetchB2BTeamAction,
  selectB2BTeamMemberAction,
  updateB2BTeamMemberAction,
} from '../../../store/actions/b2bTeam';
import { clearActiveMember, setFilters } from '../../../store/reducers/b2bTeam';
import {
  activeB2BTeamMemberIdSelector,
  activeB2BTeamMemberSelector,
  activeB2BTeamMembersCountSelector,
  b2bTeamFiltersSelector,
  b2bTeamMembersSelector,
  deletingB2BTeamMemberIdSelector,
  filteredB2BTeamMembersSelector,
  isB2BActiveTeamMemberLoadingSelector,
  isB2BTeamLoadingSelector,
  isB2BTeamSavingSelector,
} from '../../../store/selectors/b2bTeam';

export { AGENT_TEAM_PATH, AGENT_TEAM_ROUTE } from './routes';

type EditorMode = 'create' | 'edit' | null;

const ROLE_LABELS: Record<B2BTeamMemberRole, string> = {
  administrator: 'Администратор',
  agent: 'Турагент',
};

const STATUS_LABELS: Record<B2BTeamMemberStatus, string> = {
  active: 'Активен',
  invited: 'Приглашён',
  disabled: 'Отключён',
};

const ROLE_OPTIONS = [
  { title: ROLE_LABELS.agent, value: 'agent' },
  { title: ROLE_LABELS.administrator, value: 'administrator' },
];

const STATUS_OPTIONS = [
  { title: STATUS_LABELS.active, value: 'active' },
  { title: STATUS_LABELS.invited, value: 'invited' },
  { title: STATUS_LABELS.disabled, value: 'disabled' },
];

const ROLE_PERMISSIONS: Record<B2BTeamMemberRole, string[]> = {
  administrator: [
    'Все заказы и клиенты',
    'Управление сотрудниками и ролями',
    'Доступ к настройкам агентства',
  ],
  agent: [
    'Работа с заказами агентства',
    'Создание и сопровождение бронирований',
    'Работа с клиентской базой',
  ],
};

const EMPTY_MEMBER: B2BTeamMemberDraft = {
  fullName: '',
  email: '',
  phone: '',
  role: 'agent',
  status: 'invited',
};

const TeamPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { member_id: memberIdFromUrl } = useParams<{
    member_id?: string;
  }>();
  const members = useSelector(b2bTeamMembersSelector);
  const filteredMembers = useSelector(filteredB2BTeamMembersSelector);
  const selectedMemberId = useSelector(activeB2BTeamMemberIdSelector);
  const selectedMember = useSelector(activeB2BTeamMemberSelector);
  const activeMembersCount = useSelector(activeB2BTeamMembersCountSelector);
  const { query } = useSelector(b2bTeamFiltersSelector);
  const isLoading = useSelector(isB2BTeamLoadingSelector);
  const isLoadingActiveMember = useSelector(
    isB2BActiveTeamMemberLoadingSelector
  );
  const isSaving = useSelector(isB2BTeamSavingSelector);
  const deletingMemberId = useSelector(deletingB2BTeamMemberIdSelector);
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [form, setForm] = useState<B2BTeamMemberDraft>(EMPTY_MEMBER);
  const [isDeleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);

  useEffect(() => {
    if (!members.length) {
      dispatch(fetchB2BTeamAction());
    }
  }, [dispatch, members.length]);

  useEffect(() => {
    if (!memberIdFromUrl) {
      if (selectedMemberId || selectedMember || isLoadingActiveMember) {
        dispatch(clearActiveMember());
      }

      return;
    }

    setEditorMode(null);
    setDeleteConfirmationVisible(false);

    if (!members.length) return;

    const memberFromUrl = members.find(({ id }) => id === memberIdFromUrl);

    if (!memberFromUrl) {
      history.replace(AGENT_TEAM_ROUTE);
      dispatch(clearActiveMember());
      return;
    }

    if (selectedMemberId !== memberFromUrl.id) {
      dispatch(selectB2BTeamMemberAction({ id: memberFromUrl.id }));
    }
  }, [
    dispatch,
    history,
    isLoadingActiveMember,
    memberIdFromUrl,
    members,
    selectedMember,
    selectedMemberId,
  ]);

  const handleSelectMember = (id: string) => {
    if (id === selectedMemberId && !editorMode) return;

    setEditorMode(null);
    setDeleteConfirmationVisible(false);
    history.push(`${AGENT_TEAM_ROUTE}/${id}`);
    dispatch(selectB2BTeamMemberAction({ id }));
  };

  const handleCreate = () => {
    history.push(AGENT_TEAM_ROUTE);
    dispatch(clearActiveMember());
    setForm(EMPTY_MEMBER);
    setEditorMode('create');
    setDeleteConfirmationVisible(false);
  };

  const handleEdit = () => {
    if (!selectedMember) return;

    setForm({
      fullName: selectedMember.fullName,
      email: selectedMember.email,
      phone: selectedMember.phone,
      role: selectedMember.role,
      status: selectedMember.status,
    });
    setEditorMode('edit');
    setDeleteConfirmationVisible(false);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (editorMode === 'create') {
      dispatch(
        createB2BTeamMemberAction({
          id: `agent-${Date.now()}`,
          member: form,
        })
      );
    }

    if (editorMode === 'edit' && selectedMember) {
      dispatch(
        updateB2BTeamMemberAction({
          member: {
            ...selectedMember,
            ...form,
          },
        })
      );
    }

    setEditorMode(null);
  };

  const handleDelete = () => {
    if (!selectedMember) return;

    dispatch(deleteB2BTeamMemberAction({ id: selectedMember.id }));
    history.push(AGENT_TEAM_ROUTE);
    setDeleteConfirmationVisible(false);
  };

  return (
    <Page>
      <Topbar>
        <div>
          <Title>Команда и доступы</Title>
          <Muted>
            {activeMembersCount} активных сотрудников из {members.length}
          </Muted>
        </div>
        <CreateButton variant="primary" size="tiny" onClick={handleCreate}>
          <PlusIcon />
          Добавить турагента
        </CreateButton>
      </Topbar>

      <Content>
        <MemberList>
          <ListSearch
            aria-label="Поиск сотрудников"
            placeholder="Имя, телефон или email…"
            value={query}
            onChange={(event) =>
              dispatch(setFilters({ query: event.target.value }))
            }
          />
          <ListBody>
            {isLoading && !members.length ? (
              <ListLoader />
            ) : filteredMembers.length ? (
              filteredMembers.map((member) => (
                <MemberRow
                  key={member.id}
                  type="button"
                  $active={member.id === selectedMemberId}
                  disabled={member.id === deletingMemberId}
                  onClick={() => handleSelectMember(member.id)}
                >
                  <ListAvatar name={member.fullName} url={null} />
                  <MemberRowInfo>
                    <strong>{member.fullName}</strong>
                    <span>{ROLE_LABELS[member.role]}</span>
                    <small>{member.email}</small>
                  </MemberRowInfo>
                  <RowAside>
                    <StatusDot $status={member.status} />
                    <StyledArrowRight />
                  </RowAside>
                </MemberRow>
              ))
            ) : (
              <EmptyList>
                <strong>Сотрудники не найдены</strong>
                <Muted>Измените поисковый запрос</Muted>
              </EmptyList>
            )}
          </ListBody>
        </MemberList>

        <DetailsPanel>
          {isLoadingActiveMember || isSaving ? (
            <DetailsLoader />
          ) : editorMode ? (
            <MemberEditor
              form={form}
              mode={editorMode}
              onCancel={() => setEditorMode(null)}
              onChange={setForm}
              onSubmit={handleSubmit}
            />
          ) : selectedMember ? (
            <MemberDetails
              member={selectedMember}
              isDeleteConfirmationVisible={isDeleteConfirmationVisible}
              isDeleting={deletingMemberId === selectedMember.id}
              onCancelDelete={() => setDeleteConfirmationVisible(false)}
              onConfirmDelete={handleDelete}
              onDelete={() => setDeleteConfirmationVisible(true)}
              onEdit={handleEdit}
            />
          ) : (
            <EmptyDetails>
              <strong>Выберите сотрудника</strong>
              <Muted>
                Здесь можно изменить контакты, роль и доступ турагента.
              </Muted>
            </EmptyDetails>
          )}
        </DetailsPanel>
      </Content>
    </Page>
  );
};

interface MemberEditorProps {
  form: B2BTeamMemberDraft;
  mode: Exclude<EditorMode, null>;
  onCancel: () => void;
  onChange: (form: B2BTeamMemberDraft) => void;
  onSubmit: (event: FormEvent) => void;
}

const MemberEditor: React.FC<MemberEditorProps> = ({
  form,
  mode,
  onCancel,
  onChange,
  onSubmit,
}) => (
  <Editor onSubmit={onSubmit}>
    <DetailsHeader>
      <div>
        <Muted>
          {mode === 'create' ? 'Новый сотрудник' : 'Редактирование'}
        </Muted>
        <h2>{mode === 'create' ? 'Добавить турагента' : form.fullName}</h2>
      </div>
    </DetailsHeader>
    <EditorBody>
      <Block>
        <BlockTitle>Данные сотрудника</BlockTitle>
        <Fields>
          <Field>
            <span>Имя и фамилия</span>
            <FieldInput
              required
              value={form.fullName}
              onChange={(event) =>
                onChange({ ...form, fullName: event.target.value })
              }
            />
          </Field>
          <Field>
            <span>Рабочая почта</span>
            <FieldInput
              required
              type="email"
              value={form.email}
              onChange={(event) =>
                onChange({ ...form, email: event.target.value })
              }
            />
          </Field>
          <Field>
            <span>Телефон</span>
            <FieldInput
              required
              type="tel"
              value={form.phone}
              onChange={(event) =>
                onChange({ ...form, phone: event.target.value })
              }
            />
          </Field>
          <Field>
            <span>Роль</span>
            <FieldSelect
              options={ROLE_OPTIONS}
              value={form.role}
              onChange={(event: CustomEvent<{ value: B2BTeamMemberRole }>) =>
                onChange({ ...form, role: event.detail.value })
              }
            />
          </Field>
          <Field>
            <span>Статус доступа</span>
            <FieldSelect
              options={STATUS_OPTIONS}
              value={form.status}
              onChange={(event: CustomEvent<{ value: B2BTeamMemberStatus }>) =>
                onChange({ ...form, status: event.detail.value })
              }
            />
          </Field>
        </Fields>
      </Block>
      <InviteHint>
        {mode === 'create'
          ? 'После сохранения сотрудник появится в команде, а приглашение на создание пароля будет считаться отправленным.'
          : 'Изменения роли и статуса будут применены к доступу сотрудника.'}
      </InviteHint>
    </EditorBody>
    <EditorActions>
      <CancelButton size="tiny" onClick={onCancel}>
        Отмена
      </CancelButton>
      <SaveButton type="submit" variant="primary" size="tiny">
        {mode === 'create' ? 'Добавить' : 'Сохранить'}
      </SaveButton>
    </EditorActions>
  </Editor>
);

interface MemberDetailsProps {
  member: B2BTeamMember;
  isDeleteConfirmationVisible: boolean;
  isDeleting: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

const MemberDetails: React.FC<MemberDetailsProps> = ({
  member,
  isDeleteConfirmationVisible,
  isDeleting,
  onCancelDelete,
  onConfirmDelete,
  onDelete,
  onEdit,
}) => (
  <Details>
    <MemberHeader>
      <HeaderAvatar name={member.fullName} url={null} />
      <div>
        <Muted>Сотрудник · {member.id}</Muted>
        <h2>{member.fullName}</h2>
        <HeaderBadges>
          <RoleBadge>{ROLE_LABELS[member.role]}</RoleBadge>
          <StatusBadge $status={member.status}>
            <StatusDot $status={member.status} />
            {STATUS_LABELS[member.status]}
          </StatusBadge>
        </HeaderBadges>
      </div>
      <HeaderActions>
        <EditButton size="tiny" onClick={onEdit}>
          Редактировать
        </EditButton>
        <DeleteButton variant="error" size="tiny" onClick={onDelete}>
          Удалить
        </DeleteButton>
      </HeaderActions>
    </MemberHeader>

    <DetailsBody>
      {isDeleteConfirmationVisible && (
        <DeleteConfirmation>
          <div>
            <strong>Удалить сотрудника из команды?</strong>
            <span>Доступ к агентской CRM будет отозван.</span>
          </div>
          <DeleteConfirmationActions>
            <SmallButton size="tiny" onClick={onCancelDelete}>
              Отмена
            </SmallButton>
            <SmallDeleteButton
              variant="error"
              size="tiny"
              disabled={isDeleting}
              onClick={onConfirmDelete}
            >
              {isDeleting ? 'Удаляем…' : 'Удалить'}
            </SmallDeleteButton>
          </DeleteConfirmationActions>
        </DeleteConfirmation>
      )}

      <DetailGrid>
        <Block>
          <BlockTitle>Контактные данные</BlockTitle>
          <InfoGrid>
            <Info>
              <span>Рабочая почта</span>
              <strong>{member.email}</strong>
            </Info>
            <Info>
              <span>Телефон</span>
              <strong>{member.phone}</strong>
            </Info>
            <Info>
              <span>В команде с</span>
              <strong>{member.createdAt}</strong>
            </Info>
            <Info>
              <span>Последняя активность</span>
              <strong>{member.lastSeen}</strong>
            </Info>
          </InfoGrid>
        </Block>

        <Block>
          <BlockTitle>Роль и права</BlockTitle>
          <PermissionBody>
            <RoleLine>
              <RoleBadge>{ROLE_LABELS[member.role]}</RoleBadge>
              <Muted>Базовая модель доступа</Muted>
            </RoleLine>
            <PermissionList>
              {ROLE_PERMISSIONS[member.role].map((permission) => (
                <li key={permission}>
                  <PermissionCheck />
                  {permission}
                </li>
              ))}
            </PermissionList>
          </PermissionBody>
        </Block>
      </DetailGrid>

      <AuditNote>
        <strong>Аудит действий</strong>
        <span>
          Изменения заказов, оплат и прав доступа будут фиксироваться в журнале
          после подключения бэкенда.
        </span>
      </AuditNote>
    </DetailsBody>
  </Details>
);

const Panel = styled.section`
  background: #fff;
  border: 1px solid #dfe2e6;
  border-radius: 7px;
  box-shadow: 0 1px 4px rgba(24, 35, 48, 0.05);
`;

const Page = styled.main`
  align-self: stretch;
  background: #eef0f2;
  display: grid;
  gap: 12px;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  min-height: 650px;
  padding: 12px;
  width: 100%;
`;

const Topbar = styled(Panel)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;

  > div:first-child {
    display: grid;
    gap: 3px;
  }
`;

const Title = styled.h1`
  font-size: 21px;
  line-height: 26px;
  margin: 0;
`;

const Muted = styled.div`
  color: #919499;
  font-size: 12px;
  line-height: 17px;
`;

const CreateButton = styled(Button)`
  align-items: center;
  border: none;
  display: flex;
  gap: 6px;
  height: 36px;
  justify-content: center;
  width: 166px;
`;

const PlusIcon = styled(Plus)`
  height: 14px;
  width: 14px;
`;

const Content = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 350px minmax(650px, 1fr);
  min-height: 0;
`;

const MemberList = styled(Panel)`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;
`;

const ListSearch = styled(Input)`
  border-color: #d9dce1;
  font-size: 13px;
  height: 36px;
  margin: 12px;
  padding: 0 10px;
  width: calc(100% - 24px);

  &:focus {
    border-color: #007aff;
    outline: none;
  }
`;

const ListBody = styled.div`
  border-top: 1px solid #e1e3e6;
  overflow: auto;
`;

const MemberRow = styled.button<{ $active: boolean }>`
  align-items: center;
  background: ${({ $active }) => ($active ? '#f1f7ff' : '#fff')};
  border: 0;
  border-bottom: 1px solid #eceef0;
  border-left: 3px solid
    ${({ $active }) => ($active ? '#007aff' : 'transparent')};
  cursor: pointer;
  display: grid;
  font-family: inherit;
  gap: 10px;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  padding: 12px;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${({ $active }) => ($active ? '#f1f7ff' : '#f6f8fa')};
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

const ListAvatar = styled(Avatar)`
  height: 38px;
  width: 38px;
`;

const MemberRowInfo = styled.div`
  display: grid;
  gap: 3px;
  min-width: 0;

  strong,
  span,
  small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span,
  small {
    color: #919499;
  }
`;

const RowAside = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const StatusDot = styled.i<{ $status: B2BTeamMemberStatus }>`
  background: ${({ $status }) => {
    if ($status === 'active') return '#57a900';
    if ($status === 'invited') return '#f7aa1b';
    return '#a3a6aa';
  }};
  border-radius: 50%;
  display: inline-block;
  height: 7px;
  width: 7px;
`;

const StyledArrowRight = styled(ArrowRight)`
  color: #a3a6aa;
  height: 17px;
  width: 17px;
`;

const EmptyList = styled.div`
  display: grid;
  gap: 5px;
  justify-items: center;
  padding: 44px 20px;
  text-align: center;
`;

const ListLoader = styled(Loader)`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 220px;
  width: 100%;
`;

const DetailsPanel = styled(Panel)`
  min-width: 0;
  overflow: hidden;
`;

const DetailsLoader = styled(Loader)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  min-height: 420px;
  width: 100%;
`;

const EmptyDetails = styled.div`
  align-content: center;
  display: grid;
  gap: 7px;
  height: 100%;
  justify-items: center;
  padding: 30px;
  text-align: center;
`;

const Details = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
`;

const DetailsHeader = styled.div`
  border-bottom: 1px solid #e1e3e6;
  padding: 17px 19px;

  h2 {
    font-size: 20px;
    margin: 3px 0 0;
  }
`;

const MemberHeader = styled(DetailsHeader)`
  align-items: center;
  display: grid;
  gap: 13px;
  grid-template-columns: 58px minmax(0, 1fr) auto;
`;

const HeaderAvatar = styled(Avatar)`
  height: 58px;
  width: 58px;
`;

const HeaderBadges = styled.div`
  align-items: center;
  display: flex;
  gap: 7px;
`;

const RoleBadge = styled.span`
  background: #eef4fb;
  border-radius: 4px;
  color: #315f8c;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 7px;
`;

const StatusBadge = styled.span<{ $status: B2BTeamMemberStatus }>`
  align-items: center;
  color: ${({ $status }) => ($status === 'active' ? '#4f9700' : '#777b80')};
  display: inline-flex;
  font-size: 11px;
  gap: 5px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled(Button)`
  width: 116px;
`;

const DeleteButton = styled(Button)`
  width: 80px;
`;

const DetailsBody = styled.div`
  background: #f0f1f2;
  min-height: 0;
  overflow: auto;
  padding: 12px;
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(360px, 1.1fr) minmax(300px, 0.9fr);
`;

const Block = styled.section`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const BlockTitle = styled.h3`
  border-bottom: 1px solid #eceef0;
  font-size: 15px;
  margin: 0;
  padding: 13px 14px;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 18px 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 16px;
`;

const Info = styled.div`
  display: grid;
  gap: 5px;
  min-width: 0;

  span {
    color: #919499;
    font-size: 11px;
  }

  strong {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const PermissionBody = styled.div`
  padding: 14px;
`;

const RoleLine = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const PermissionList = styled.ul`
  display: grid;
  gap: 10px;
  list-style: none;
  margin: 14px 0 0;
  padding: 0;

  li {
    align-items: center;
    display: flex;
    font-size: 12px;
    gap: 8px;
  }
`;

const PermissionCheck = styled(Check)`
  color: #57a900;
  height: 14px;
  width: 14px;

  path {
    stroke: currentColor;
  }
`;

const AuditNote = styled.div`
  background: #fff7e8;
  border-radius: 5px;
  color: #775522;
  display: grid;
  gap: 5px;
  margin-top: 12px;
  padding: 12px;

  span {
    font-size: 11px;
    line-height: 16px;
  }
`;

const DeleteConfirmation = styled.div`
  align-items: center;
  background: #fff4f5;
  border: 1px solid #f3c8cd;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 11px 12px;

  > div:first-child {
    display: grid;
    gap: 4px;
  }

  span {
    color: #777b80;
    font-size: 11px;
  }
`;

const DeleteConfirmationActions = styled.div`
  display: flex;
  gap: 7px;
`;

const SmallButton = styled(Button)`
  width: 72px;
`;

const SmallDeleteButton = styled(Button)`
  width: 82px;
`;

const Editor = styled.form`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  height: 100%;
`;

const EditorBody = styled.div`
  background: #f0f1f2;
  min-height: 0;
  overflow: auto;
  padding: 12px;
`;

const Fields = styled.div`
  display: grid;
  gap: 14px 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 16px;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;

  > span {
    color: #74777c;
    font-size: 11px;
  }
`;

const FieldInput = styled(Input)`
  border-color: #d6d9de;
  font-size: 13px;
  height: 38px;
  padding: 0 10px;
  width: 100%;

  &:focus {
    border-color: #007aff;
    outline: none;
  }
`;

const FieldSelect = styled(Select)`
  border: 1px solid #d6d9de;
  width: 100%;

  > button {
    width: 100%;
  }
`;

const InviteHint = styled.div`
  background: #f3f7fb;
  border-radius: 5px;
  color: #74777c;
  font-size: 12px;
  line-height: 18px;
  margin-top: 12px;
  padding: 11px 12px;
`;

const EditorActions = styled.div`
  border-top: 1px solid #e1e3e6;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 16px;
`;

const CancelButton = styled(Button)`
  width: 92px;
`;

const SaveButton = styled(Button)`
  border: none;
  width: 108px;
`;

export default TeamPage;
